import React, { forwardRef, useEffect } from "react";
import styles from "./DateSelector.module.scss";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import TextInput from "../TextInput/TextInput";
import { observer } from "mobx-react";
import { range } from "../../../../utils/misc";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { useStores } from "../../../../hooks/mobx";

type TDateSelector = {
  value: string;
  maxDate?: Date;
  minDate?: Date;
  placeholder: string;
  onChange: (v: string) => void;
  error?: string;
  todayButton?: string;
  withInitial?: boolean;
};

const DateSelector: React.FC<TDateSelector> = observer(
  ({
    onChange,
    value,
    maxDate,
    placeholder,
    error,
    minDate,
    withInitial,
    todayButton,
  }) => {
    const { uiStore } = useStores();
    const { isMobile } = uiStore;
    registerLocale("ru", ru);

    useEffect(() => {
      if (withInitial && maxDate)
        onChange(moment(maxDate).format("YYYY-MM-DD"));
    }, []);

    const onDateChange = (date: Date) => {
      onChange(moment(date).format("YYYY-MM-DD"));
    };

    const ExampleCustomInput = forwardRef(
      ({ onClick }: { onClick?: () => void }, ref) => (
        <TextInput
          value={value ? moment(value).format("DD.MM.YYYY") : ""}
          onClick={onClick}
          placeholder={placeholder}
          error={error}
          dataIcon={"date"}
          readonly
        />
      )
    );

    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];

    const years = range(1900, getYear(new Date()) + 1, 1);

    return (
      <div className={styles.DateSelector}>
        <DatePicker
          locale={"ru"}
          maxDate={maxDate}
          minDate={minDate}
          wrapperClassName={"w-100 custom-date-selector"}
          todayButton={todayButton}
          closeOnScroll={false}
          onChange={onDateChange}
          customInput={<ExampleCustomInput />}
          className={styles.DateSelector_Picker}
          selected={moment(value || undefined).toDate()}
          dayClassName={() => styles.DateSelector_Picker_Day}
          withPortal={isMobile}
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className={styles.CalendarHeader}>
              <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                className={styles.CalendarHeader_Button}
              >
                <img
                  src="/static/images/arrows/carouselLeftArrow.png"
                  alt="left"
                />
              </button>
              <select
                className={styles.CalendarHeader_Selector}
                value={getYear(date)}
                onChange={({ target: { value } }) => changeYear(value)}
              >
                {years.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <select
                className={styles.CalendarHeader_Selector}
                value={months[getMonth(date)]}
                onChange={({ target: { value } }) =>
                  changeMonth(months.indexOf(value))
                }
              >
                {months.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <button
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                className={styles.CalendarHeader_Button}
              >
                <img
                  src="/static/images/arrows/carouselRightArrow.png"
                  alt="right"
                />
              </button>
            </div>
          )}
        />
      </div>
    );
  }
);

export default DateSelector;
